import { reportingYearWindow } from '../../../shared/utils/reportingYear';
import { normalizeMissingDependencies } from './missingDependencies';

export const DATA_SOURCE_IDS = {
  SCOPE_1_FUEL: 'scope1_fuel',
  SCOPE_2_ELECTRICITY: 'scope2_electricity',
  SCOPE_3_AF_REPORT: 'scope3_af_report',
  SCOPE_3_EWASTE: 'scope3_ewaste',
  SCOPE_3_SEAT_API: 'scope3_seat_api',
  SCOPE_3_OUTSOURCING: 'scope3_outsourcing',
  SCOPE_3_CAT3: 'scope3_cat3',
  SCOPE_3_CAT8: 'scope3_cat8',
};

/** Recalculation target id (overview recalculation_impact) per imported source. */
export const RECALCULATION_TARGET_BY_SOURCE_ID = Object.freeze({
  [DATA_SOURCE_IDS.SCOPE_1_FUEL]: 'scope1',
  [DATA_SOURCE_IDS.SCOPE_2_ELECTRICITY]: 'scope2',
  [DATA_SOURCE_IDS.SCOPE_3_EWASTE]: 'cat5',
  [DATA_SOURCE_IDS.SCOPE_3_AF_REPORT]: 'scope3_af',
});

const RECALCULATION_TARGET_IDS = Object.freeze([
  'scope1', 'scope2', 'cat3', 'scope3_af', 'cat5', 'cat19', 'cat8',
]);

const normalizeCalculatedResults = (value) => {
  const source = value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  return Object.fromEntries(RECALCULATION_TARGET_IDS.map((target) => [
    target,
    Boolean(source[target]),
  ]));
};

export const HISTORY_ENTRY_TYPES = {
  IMPORT: 'IMPORT',
  SEAT_SYNC: 'SEAT_SYNC',
  CAT8_SYNC: 'CAT8_SYNC',
  CAT19_UPDATE: 'CAT19_UPDATE',
};

const RECALCULATION_LABEL_KEYS_BY_CODE = Object.freeze({
  SCOPE_1: 'dataInput.recalculation.scope1',
  SCOPE_2: 'dataInput.recalculation.scope2',
  SCOPE_3_CATEGORY_3: 'dataInput.recalculation.cat3',
  SCOPE_3_AF: 'dataInput.recalculation.scope3Af',
  SCOPE_3_CATEGORY_5: 'dataInput.recalculation.cat5',
  SCOPE_3_CATEGORY_1_9: 'dataInput.recalculation.cat19',
  SCOPE_3_CATEGORY_8: 'dataInput.recalculation.cat8',
});

const LEGACY_RECALCULATION_LABEL_KEYS = new Set(
  [
    ...Object.values(RECALCULATION_LABEL_KEYS_BY_CODE),
    'dataInput.recalculation.scope2Cat3',
  ],
);

const HISTORY_NOTE_KEYS_BY_CODE = Object.freeze({
  MANUAL_AMOUNT: 'dataInput.history.note.manualAmount',
});

const LEGACY_HISTORY_NOTE_KEYS = new Set([
  'dataInput.history.note.recorded',
  'dataInput.history.note.monthlySync',
  'dataInput.history.note.manualSync',
  'dataInput.history.note.manualAmount',
  'dataInput.history.note.annualTotal',
]);

export const recalculationLabelKey = item => (
  RECALCULATION_LABEL_KEYS_BY_CODE[item?.label_code]
  || (LEGACY_RECALCULATION_LABEL_KEYS.has(item?.label_key) ? item.label_key : null)
  || 'dataInput.recalculation.category'
);

export const historyNoteKey = entry => (
  HISTORY_NOTE_KEYS_BY_CODE[entry?.note_code]
  || (LEGACY_HISTORY_NOTE_KEYS.has(entry?.note_key) ? entry.note_key : null)
);

const HISTORY_TYPE_BY_SOURCE = {
  [DATA_SOURCE_IDS.SCOPE_1_FUEL]: HISTORY_ENTRY_TYPES.IMPORT,
  [DATA_SOURCE_IDS.SCOPE_2_ELECTRICITY]: HISTORY_ENTRY_TYPES.IMPORT,
  [DATA_SOURCE_IDS.SCOPE_3_AF_REPORT]: HISTORY_ENTRY_TYPES.IMPORT,
  [DATA_SOURCE_IDS.SCOPE_3_EWASTE]: HISTORY_ENTRY_TYPES.IMPORT,
  [DATA_SOURCE_IDS.SCOPE_3_SEAT_API]: HISTORY_ENTRY_TYPES.SEAT_SYNC,
  [DATA_SOURCE_IDS.SCOPE_3_OUTSOURCING]: HISTORY_ENTRY_TYPES.CAT19_UPDATE,
  [DATA_SOURCE_IDS.SCOPE_3_CAT8]: HISTORY_ENTRY_TYPES.CAT8_SYNC,
};

export const DATA_INPUT_YEARS = reportingYearWindow();
export const DATA_INPUT_RECOVERY_REQUIRED = 'DATA_INPUT_RECOVERY_REQUIRED';

/** Return whether an overview read was blocked by resource quarantine. */
export const isDataInputRecoveryRequired = error => (
  Number(error?.status ?? error?.response?.status) === 503
  && error?.message === DATA_INPUT_RECOVERY_REQUIRED
);

const emptyUploadSource = () => ({
  currentName: '',
  currentMeta: '',
  currentImportId: null,
  currentDownloadAvailable: false,
  currentMetaKey: '',
  currentMetaParams: {},
  status: null,
  canImport: null,
  history: [],
});

const COUNTER_KEYS = [
  'total_rows', 'processed_rows', 'included_rows', 'inserted_rows', 'updated_rows',
  'unchanged_rows', 'skipped_rows', 'ambiguous_rows', 'filtered_rows', 'invalid_rows',
  'unclassified_rows', 'deleted_rows',
];
export const TERMINAL_RECALCULATION_STATUSES = new Set(['SUCCESS', 'FAILED']);
export const ACTIVE_RECALCULATION_STATUSES = new Set(['QUEUED', 'STARTED']);

const optionalBoolean = (value) => (typeof value === 'boolean' ? value : null);

export function normalizeNestedRecalculation(value) {
  if (!value?.outcome) return null;
  return {
    outcome: String(value.outcome).toUpperCase(),
    operationId: value.operation_id || null,
    reasonCode: value.reason_code || null,
    message: value.error_summary?.message || null,
    missingDependencies: normalizeMissingDependencies(value),
  };
}

export function normalizeOperationCounters(value = {}) {
  const source = value?.counters || value?.stats || {};
  const result = COUNTER_KEYS.reduce((normalized, key) => {
    const count = Number(source[key]);
    if (Number.isFinite(count)) normalized[key] = count;
    return normalized;
  }, {});
  if (result.deleted_rows === undefined && Number.isFinite(Number(source.deleted_count))) {
    result.deleted_rows = Number(source.deleted_count);
  }
  return result;
}

export function normalizeImportOperation(value = {}) {
  const fiscalYear = Number(value.fiscal_year ?? value.fiscalYear);
  return {
    id: value.id || value.operation_id || null,
    fiscalYear: Number.isFinite(fiscalYear) ? fiscalYear : null,
    importType: value.import_type || value.importType || null,
    fileName: value.file_name || value.original_filename || value.fileName || null,
    status: String(value.status || '').toUpperCase(),
    message: value.error_summary?.message || null,
    params: value.error_summary?.params || {},
    counters: normalizeOperationCounters(value),
    partialDataPossible: Boolean(value.partial_data_possible),
    recoveryAction: value.recovery_action || value.recoveryAction || null,
    missingDependencies: normalizeMissingDependencies(value),
    recalculation: normalizeNestedRecalculation(value.recalculation),
    stage: String(value.stage || '').toUpperCase() || null,
    canCancel: Boolean(value.can_cancel ?? value.canCancel),
    updatedAt: value.updated_at || value.updatedAt || null,
    statusUrl: value.status_url || value.statusUrl || null,
  };
}

export function normalizeActiveImport(value) {
  if (!value || !value.status) return null;
  const fiscalYear = Number(value.fiscal_year ?? value.fiscalYear);
  return {
    id: value.id || value.operation_id || null,
    fiscalYear: Number.isFinite(fiscalYear) ? fiscalYear : null,
    importType: value.import_type || value.importType || null,
    status: String(value.status || '').toUpperCase(),
    stage: String(value.stage || '').toUpperCase() || null,
    fileName: value.file_name || value.original_filename || null,
    canCancel: Boolean(value.can_cancel ?? value.canCancel),
    updatedAt: value.updated_at || value.updatedAt || null,
    statusUrl: value.status_url || value.statusUrl || null,
  };
}

export const normalizeActiveImports = (values) => (
  Array.isArray(values)
    ? values.map(normalizeActiveImport).filter((operation) => (
      operation?.id
      && operation.importType
      && operation.fiscalYear !== null
    ))
    : []
);

export function normalizeRecalculationOperation(value) {
  const operationId = value?.operation_id || value?.id;
  if (!operationId || !value.status) return null;
  const fiscalYear = Number(value.fiscal_year ?? value.fiscalYear);
  return {
    id: operationId,
    fiscalYear: Number.isFinite(fiscalYear) ? fiscalYear : null,
    status: String(value.status).toUpperCase(),
    targets: [...(value.targets || [])],
    trigger: String(value.trigger || 'MANUAL').toUpperCase(),
    requestedAt: value.created_at || null,
    startedAt: value.started_at || null,
    completedAt: value.completed_at || null,
    message: value.error_summary?.message || null,
    params: value.error_summary?.params || {},
    missingDependencies: normalizeMissingDependencies(value),
    statusUrl: value.status_url || value.statusUrl || null,
  };
}

export const normalizeActiveRecalculations = (values) => (
  Array.isArray(values)
    ? values.map(normalizeRecalculationOperation).filter((operation) => (
      operation?.id && operation.fiscalYear !== null
    ))
    : []
);

export function createEmptyDataInputState(year) {
  return {
    year,
    efPublished: false,
    scopeStatus: {},
    actionFlags: {
      canRecalculate: null,
      canSyncSeat: null,
      canSyncCat8: null,
      canEditCat19: null,
    },
    recalculationImpact: [],
    calculatedResults: {},
    lastRecalculation: null,
    activeImports: [],
    activeRecalculations: [],
    sources: {
      [DATA_SOURCE_IDS.SCOPE_1_FUEL]: emptyUploadSource(),
      [DATA_SOURCE_IDS.SCOPE_2_ELECTRICITY]: emptyUploadSource(),
      [DATA_SOURCE_IDS.SCOPE_3_AF_REPORT]: emptyUploadSource(),
      [DATA_SOURCE_IDS.SCOPE_3_EWASTE]: emptyUploadSource(),
      [DATA_SOURCE_IDS.SCOPE_3_SEAT_API]: {
        valueKg: null, totalSeats: null, workingDays: null, configVersion: null, quarter: null,
        methodology: null, requestedAsOfDate: null,
        isOfficial: false, lastSyncedAt: null, canSync: null, canManualInput: null,
        syncUnavailableReason: null, defaultSyncAsOfDate: null,
        manualMinAsOfDate: null, manualMaxAsOfDate: null, defaultManualAsOfDate: null,
        history: [],
      },
      [DATA_SOURCE_IDS.SCOPE_3_OUTSOURCING]: {
        amountJpy: 0,
        note: '',
        updatedAt: null,
        calculationStatus: 'EMPTY',
        canEdit: null,
        history: [],
      },
      [DATA_SOURCE_IDS.SCOPE_3_CAT3]: { emissions: null, lastUpdatedAt: null },
      [DATA_SOURCE_IDS.SCOPE_3_CAT8]: {
        emissions: null, quarter: null, officeCount: null, totalAreaM2: null,
        sourceAsOfDate: null, methodology: null, isOfficial: false,
        lastSyncedAt: null, lastUpdatedAt: null, canSync: null, canManualInput: null,
        syncUnavailableReason: null, defaultSyncAsOfDate: null,
        manualMinAsOfDate: null, manualMaxAsOfDate: null, defaultManualAsOfDate: null,
        history: [],
      },
    },
  };
}

const metric = (stats, key) => (
  stats?.[key] === undefined || stats?.[key] === null ? null : stats[key]
);

export const normalizeHistoryStatus = (entry = {}) => {
  const status = String(entry.status || '').toUpperCase();
  const message = entry.error_summary?.code
    || entry.error_summary?.message
    || entry.error_code
    || entry.message
    || null;
  if (entry.partial_data_possible || entry.partialDataPossible || status === 'PARTIAL') {
    return 'PARTIAL';
  }
  if (message === 'IMPORT_CANCELLED' || status === 'CANCELLED') return 'CANCELLED';
  if (status === 'READY') return 'SUCCESS';
  if (status === 'PENDING') {
    const recalculation = normalizeNestedRecalculation(
      entry.recalculation || entry.stats?.recalculation,
    );
    if (recalculation?.outcome === 'FAILED') return 'FAILED';
    if (recalculation?.outcome === 'SUCCESS'
      || recalculation?.outcome === 'SKIPPED') return 'SUCCESS';
    return 'PROCESSING';
  }
  return status;
};

export const normalizeHistory = (entries = [], sourceId) => entries.map((entry) => {
  const rawStats = entry?.stats || entry?.counters || {};
  const recalculation = normalizeNestedRecalculation(
    entry.recalculation || rawStats.recalculation,
  );
  const before = entry?.before && typeof entry.before === 'object' ? entry.before : null;
  const after = entry?.after && typeof entry.after === 'object' ? entry.after : null;
  const quarterValue = metric(rawStats, 'quarter');
  const monthValue = metric(rawStats, 'month');
  return {
    id: entry.id || entry.operation_id,
    type: HISTORY_TYPE_BY_SOURCE[sourceId] || HISTORY_ENTRY_TYPES.IMPORT,
    sourceId,
    createdAt: entry.created_at,
    createdBy: entry.actor_display || entry.created_by,
    createdByKey: entry.created_by_key,
    fileName: entry.original_filename || entry.file_name,
    fileNames: Array.isArray(entry.files)
      ? entry.files.map((file) => file.original_filename || file.file_name).filter(Boolean)
      : (entry.file_name ? [entry.file_name] : []),
    files: Array.isArray(entry.files)
      ? entry.files.map((file) => ({
        fileId: file.file_id,
        fileName: file.original_filename || file.file_name,
        fileSha256: file.file_sha256,
      })).filter((file) => file.fileName)
      : [],
    downloadAvailable: Boolean(entry.download_available),
    partialDataPossible: Boolean(entry.partial_data_possible),
    recoveryAction: entry.recovery_action || null,
    recalculation,
    message: entry.error_summary?.code
      || entry.error_summary?.message
      || entry.error_code
      || entry.message
      || null,
    params: entry.error_summary?.params || entry.params || {},
    note: entry.note || null,
    noteCode: entry.note_code || null,
    noteKey: historyNoteKey(entry),
    month: monthValue != null && Number.isInteger(Number(monthValue))
      ? Number(monthValue)
      : null,
    quarter: quarterValue != null && Number.isInteger(Number(quarterValue))
      ? Number(quarterValue)
      : null,
    status: normalizeHistoryStatus({ ...entry, recalculation }),
    changeType: String(entry.change_type || '').toUpperCase() || null,
    inputMethod: String(
      entry.input_method || rawStats.input_method || 'SOURCE_SYNC',
    ).toUpperCase(),
    stats: normalizeOperationCounters(entry),
    operationId: entry.operation_id || entry.stats?.operation_id || null,
    snapshotId: entry.snapshot_id || entry.stats?.snapshot_id || null,
    operationOutcome: String(
      entry.operation_outcome || entry.stats?.operation_outcome || '',
    ).toUpperCase() || null,
    reusedSnapshot: Boolean(entry.reused_snapshot || entry.stats?.reused_snapshot),
    metrics: {
      totalSeats: metric(rawStats, 'total_seats'),
      workingDays: metric(rawStats, 'working_days'),
      configVersion: metric(rawStats, 'config_version'),
      requestedAsOfDate: metric(rawStats, 'requested_as_of_date'),
      sourceAsOfDate: metric(rawStats, 'source_as_of_date'),
      officeCount: metric(rawStats, 'office_count'),
      totalAreaM2: metric(rawStats, 'total_area_m2'),
      amountJpy: after?.amount_jpy ?? metric(rawStats, 'amount_jpy'),
    },
    detail: before || after ? { before, after } : null,
  };
});

const normalizeUploadSource = (source = {}, sourceId) => {
  const history = normalizeHistory(source.history, sourceId);
  const currentImport = history.find((entry) => entry.status === 'SUCCESS') || null;
  return {
    currentName: source.current_name || '',
    currentMeta: source.current_meta || '',
    currentImportId: currentImport?.id || null,
    currentDownloadAvailable: Boolean(currentImport?.downloadAvailable),
    currentMetaKey: '',
    currentMetaParams: {},
    status: String(
      source.status || source.latest_operation?.status || history[0]?.status || '',
    ).toUpperCase() || null,
    canImport: optionalBoolean(
      source.can_import ?? source.action_flags?.can_import ?? source.ef_resolvable,
    ),
    history,
  };
};

const normalizeScopeStatus = (scopeStatus = {}) => Object.entries(scopeStatus)
  .reduce((result, [key, value]) => {
    const normalizedKey = key.replace(/_/g, '').toLowerCase();
    const scope = typeof value === 'object' && value !== null ? value : {};
    const status = String(scope.status || '').toUpperCase() || null;
    let attentionCount = null;
    if (Number.isFinite(Number(scope.attention_count))) {
      attentionCount = Number(scope.attention_count);
    } else if (status === 'COMPLETE') {
      attentionCount = 0;
    } else if (status === 'PENDING' || status === 'ATTENTION') {
      attentionCount = 1;
    } else if (Number.isFinite(Number(value))) {
      attentionCount = Number(value);
    }
    result[normalizedKey] = { status, attentionCount };
    return result;
  }, {});

/** Convert the backend's snake_case overview contract into the page view model. */
export function normalizeDataInputOverview(payload, fallbackYear) {
  const empty = createEmptyDataInputState(payload?.fiscal_year || fallbackYear);
  const sources = payload?.sources || {};
  const actions = payload?.action_flags || payload?.actions || {};
  const lastRecalculation = normalizeRecalculationOperation(payload?.last_recalculation);

  return {
    ...empty,
    efPublished: Boolean(payload?.ef_resolvable ?? payload?.ef_published),
    activeImports: normalizeActiveImports(payload?.active_imports),
    activeRecalculations: normalizeActiveRecalculations(payload?.active_recalculations),
    scopeStatus: normalizeScopeStatus(payload?.scope_status),
    actionFlags: {
      canRecalculate: optionalBoolean(actions.can_recalculate),
      canSyncSeat: optionalBoolean(actions.can_sync_seat ?? actions.can_sync_drivers),
      canSyncCat8: optionalBoolean(actions.can_sync_cat8),
      canEditCat19: optionalBoolean(actions.can_edit_cat_1_9),
    },
    recalculationImpact: (payload?.recalculation_impact || []).map((item) => ({
      id: item.id,
      labelCode: item.label_code || null,
      labelKey: recalculationLabelKey(item),
      current: Number(item.current_co2 ?? item.current) || 0,
      records: Number(item.result_records ?? item.records) || 0,
      hasCalculatedResults: Boolean(item.has_calculated_results),
      inputRecords: Number(item.input_records) || 0,
      resultSources: [...(item.result_sources || [])],
      readiness: String(item.readiness || 'TARGET_NOT_READY').toUpperCase(),
      selectable: item.selectable !== false,
      defaultSelected: Boolean(item.selectable !== false && item.default_selected),
      warnings: [...(item.warnings || [])],
      missingDependencies: normalizeMissingDependencies(item),
      message: item.message || null,
      lastRecalculation: normalizeRecalculationOperation(item.last_recalculation),
    })),
    lastRecalculation: TERMINAL_RECALCULATION_STATUSES.has(lastRecalculation?.status)
      ? lastRecalculation
      : null,
    calculatedResults: normalizeCalculatedResults(payload?.calculated_results),
    sources: {
      ...empty.sources,
      [DATA_SOURCE_IDS.SCOPE_1_FUEL]: normalizeUploadSource(
        sources.scope1_fuel, DATA_SOURCE_IDS.SCOPE_1_FUEL,
      ),
      [DATA_SOURCE_IDS.SCOPE_2_ELECTRICITY]: normalizeUploadSource(
        sources.scope2_electricity, DATA_SOURCE_IDS.SCOPE_2_ELECTRICITY,
      ),
      [DATA_SOURCE_IDS.SCOPE_3_AF_REPORT]: normalizeUploadSource(
        sources.scope3_af_report, DATA_SOURCE_IDS.SCOPE_3_AF_REPORT,
      ),
      [DATA_SOURCE_IDS.SCOPE_3_EWASTE]: normalizeUploadSource(
        sources.scope3_ewaste, DATA_SOURCE_IDS.SCOPE_3_EWASTE,
      ),
      [DATA_SOURCE_IDS.SCOPE_3_SEAT_API]: {
        valueKg: sources.scope3_seat_api?.value_kg ?? null,
        totalSeats: sources.scope3_seat_api?.total_seats ?? null,
        workingDays: sources.scope3_seat_api?.working_days ?? null,
        methodology: sources.scope3_seat_api?.methodology || null,
        configVersion: sources.scope3_seat_api?.config_version ?? null,
        quarter: sources.scope3_seat_api?.quarter ?? null,
        requestedAsOfDate: sources.scope3_seat_api?.requested_as_of_date || null,
        isOfficial: Boolean(sources.scope3_seat_api?.is_official),
        lastSyncedAt: sources.scope3_seat_api?.last_synced_at || null,
        canSync: optionalBoolean(sources.scope3_seat_api?.can_sync),
        canManualInput: optionalBoolean(sources.scope3_seat_api?.can_manual_input),
        syncUnavailableReason: sources.scope3_seat_api?.sync_unavailable_reason || null,
        defaultSyncAsOfDate: sources.scope3_seat_api?.default_sync_as_of_date || null,
        manualMinAsOfDate: sources.scope3_seat_api?.manual_min_as_of_date || null,
        manualMaxAsOfDate: sources.scope3_seat_api?.manual_max_as_of_date || null,
        defaultManualAsOfDate: sources.scope3_seat_api?.default_manual_as_of_date || null,
        history: normalizeHistory(
          sources.scope3_seat_api?.history, DATA_SOURCE_IDS.SCOPE_3_SEAT_API,
        ),
      },
      [DATA_SOURCE_IDS.SCOPE_3_OUTSOURCING]: {
        amountJpy: sources.scope3_outsourcing?.amount_jpy || 0,
        note: sources.scope3_outsourcing?.note || '',
        updatedAt: sources.scope3_outsourcing?.updated_at || null,
        calculationStatus: String(
          sources.scope3_outsourcing?.calculation_status || 'EMPTY',
        ).toUpperCase(),
        canEdit: optionalBoolean(sources.scope3_outsourcing?.can_edit),
        history: normalizeHistory(
          sources.scope3_outsourcing?.history, DATA_SOURCE_IDS.SCOPE_3_OUTSOURCING,
        ),
      },
      [DATA_SOURCE_IDS.SCOPE_3_CAT3]: {
        emissions: sources.scope3_cat3?.emissions ?? null,
        rawTotal: sources.scope3_cat3?.raw_total ?? null,
        rawUnit: sources.scope3_cat3?.raw_unit || null,
        lastUpdatedAt: sources.scope3_cat3?.last_updated_at || null,
      },
      [DATA_SOURCE_IDS.SCOPE_3_CAT8]: {
        emissions: sources.scope3_cat8?.emissions ?? null,
        quarter: sources.scope3_cat8?.quarter ?? null,
        officeCount: sources.scope3_cat8?.office_count ?? null,
        totalAreaM2: sources.scope3_cat8?.total_area_m2 ?? null,
        sourceAsOfDate: sources.scope3_cat8?.source_as_of_date || null,
        methodology: sources.scope3_cat8?.methodology || null,
        lastSyncedAt: sources.scope3_cat8?.last_synced_at || null,
        lastUpdatedAt: sources.scope3_cat8?.last_updated_at || null,
        isOfficial: Boolean(sources.scope3_cat8?.is_official),
        canSync: optionalBoolean(sources.scope3_cat8?.can_sync),
        canManualInput: optionalBoolean(sources.scope3_cat8?.can_manual_input),
        syncUnavailableReason: sources.scope3_cat8?.sync_unavailable_reason || null,
        defaultSyncAsOfDate: sources.scope3_cat8?.default_sync_as_of_date || null,
        manualMinAsOfDate: sources.scope3_cat8?.manual_min_as_of_date || null,
        manualMaxAsOfDate: sources.scope3_cat8?.manual_max_as_of_date || null,
        defaultManualAsOfDate: sources.scope3_cat8?.default_manual_as_of_date || null,
        history: normalizeHistory(
          sources.scope3_cat8?.history, DATA_SOURCE_IDS.SCOPE_3_CAT8,
        ),
      },
    },
  };
}

/** Build the diagnostics-only view available while business data is quarantined. */
export function normalizeDataInputRecoveryState(
  importHistory,
  recalculationHistory,
  fallbackYear,
) {
  const operations = Array.isArray(recalculationHistory) ? recalculationHistory : [];
  const activeRecalculations = operations.filter(operation => (
    ACTIVE_RECALCULATION_STATUSES.has(String(operation?.status || '').toUpperCase())
  ));
  const lastRecalculation = operations.find(operation => (
    TERMINAL_RECALCULATION_STATUSES.has(String(operation?.status || '').toUpperCase())
  ));
  return normalizeDataInputOverview({
    fiscal_year: importHistory?.fiscal_year || fallbackYear,
    sources: importHistory?.sources || {},
    active_recalculations: activeRecalculations,
    last_recalculation: lastRecalculation || null,
  }, fallbackYear);
}

export function getScopeAttentionCount(yearState, scopeId, fallback = 0) {
  const count = yearState?.scopeStatus?.[String(scopeId).replace(/_/g, '').toLowerCase()]?.attentionCount;
  return Number.isFinite(count) ? count : fallback;
}

export function getScope3AttentionCount(yearState) {
  if (!yearState) return 0;
  const sources = yearState.sources;
  const fallback = [DATA_SOURCE_IDS.SCOPE_3_AF_REPORT, DATA_SOURCE_IDS.SCOPE_3_EWASTE]
    .filter((sourceId) => !sources[sourceId].currentName)
    .length;
  return getScopeAttentionCount(yearState, 'scope3', fallback);
}
